import React, { useCallback, useEffect, useContext } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import DropDownMenuContext from '@contexts/DropDownMenuContext';
import useDropDownMenu from '@hooks/useDropDownMenu';
import DropDownMenu from '@components/2-molecules/DropDownMenu';

export const DROP_DOWN_MENU_CONTAINER_ID = 'drop-down-menu-container';

const DropDownMenuPortal = ({ menuId, onClose, ...dropDownMenuProps }) => {
  const { resetDropDown } = useContext(DropDownMenuContext);
  const {
    dropDownMenuId,
    dropDownMenuIsOpen,
    dropDownMenuStyle,
    hideDropDownMenu,
    calculateDropDownMenuPosition,
    handleDropDownMenuResize,
    handleDropDownKeyDown,
  } = useDropDownMenu({ offsetX: dropDownMenuProps?.offsetX });
  const isCurrentDropDownMenu = dropDownMenuId === menuId;

  const dropDownMenuPortalContainer = document.getElementById(
    DROP_DOWN_MENU_CONTAINER_ID,
  );

  const handleCloseDropDownMenu = useCallback(() => {
    hideDropDownMenu();
    onClose?.();
  }, [hideDropDownMenu, onClose]);

  useEffect(() => {
    if (dropDownMenuIsOpen && isCurrentDropDownMenu) {
      globalThis.addEventListener('keydown', handleDropDownKeyDown);
      globalThis.addEventListener('resize', handleDropDownMenuResize);
      globalThis.addEventListener('scroll', handleDropDownMenuResize);

      return () => {
        globalThis.removeEventListener('keydown', handleDropDownKeyDown);
        globalThis.removeEventListener('resize', handleDropDownMenuResize);
        globalThis.removeEventListener('scroll', handleDropDownMenuResize);
      };
    }
  }, [
    calculateDropDownMenuPosition,
    dropDownMenuIsOpen,
    handleDropDownKeyDown,
    handleDropDownMenuResize,
    hideDropDownMenu,
    isCurrentDropDownMenu,
  ]);

  useEffect(() => {
    if (dropDownMenuIsOpen && isCurrentDropDownMenu) {
      calculateDropDownMenuPosition();
    }
  }, [
    // Commented out to avoid infinite loop. Revisit this if needed.
    // dropDownMenuProps.menuItems,
    calculateDropDownMenuPosition,
    dropDownMenuIsOpen,
    isCurrentDropDownMenu,
  ]);

  useEffect(() => {
    return () => {
      if (dropDownMenuIsOpen) {
        resetDropDown();
      }
    };
  }, [dropDownMenuIsOpen, resetDropDown]);

  if (!dropDownMenuPortalContainer || !isCurrentDropDownMenu) return null;

  return createPortal(
    <DropDownMenu
      {...dropDownMenuProps}
      menuId={dropDownMenuId}
      show={dropDownMenuIsOpen}
      style={dropDownMenuStyle}
      closeDropDownMenu={handleCloseDropDownMenu}
    />,
    dropDownMenuPortalContainer,
  );
};

DropDownMenuPortal.propTypes = {
  menuId: PropTypes.string.isRequired,
  onClose: PropTypes.func,
};

export default DropDownMenuPortal;
